import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Lock, Image as ImageIcon, Trash2 } from 'lucide-react'
import type { WireArticle } from '../../api/types'
import { useToggleSavedArticle } from '../../api/hooks'
import { BookmarkToggle } from './BookmarkToggle'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Diminished cards (title-only access) come back with `thumbnail: null` —
// distinct from articles that simply have no thumbnail (`thumbnail` absent).
// Only the former should read as "your access to this changed."
function isDiminishedCard(article: WireArticle): boolean {
  return 'thumbnail' in article && article.thumbnail == null
}

interface MyWireArticleRowProps {
  article: WireArticle
  /** When provided, renders a trash button that calls this instead of the bookmark toggle. */
  onRemove?: (article: WireArticle) => void
}

export function MyWireArticleRow({ article, onRemove }: MyWireArticleRowProps) {
  const [saved, setSaved] = useState(article.saved ?? false)
  const toggleSaved = useToggleSavedArticle()

  function handleToggle() {
    const nextSaved = !saved
    setSaved(nextSaved)
    toggleSaved.mutate(
      { article, nextSaved },
      {
        onSuccess: (result) => setSaved(result.saved),
        onError: () => setSaved(!nextSaved),
      },
    )
  }

  const id = article.articleId ?? article.id
  const thumbUrl = article.thumbnail?.presignedUrl
  const diminished = isDiminishedCard(article)
  const byline = article.byline || (article.authors?.length ? `By ${article.authors.join(', ')}` : null)
  const meta = [byline, article.readTime ? `${article.readTime} min read` : null, formatDate(article.publishedAt ?? article.updatedAt)]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="group flex items-center gap-3 rounded-lg border border-line bg-surface p-2.5 pr-3 transition-colors hover:border-line-strong hover:shadow-sm">
      <Link to={`/wire/articles/${id}`} className="flex min-w-0 flex-1 items-center gap-3">
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
        ) : diminished ? (
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-fill"
            title="Your access to this article has changed since you saved or read it"
          >
            <Lock size={16} className="text-warning-fg" aria-hidden="true" />
          </div>
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-fill">
            <ImageIcon size={16} className="text-fg-secondary opacity-30" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-fg group-hover:underline">{article.title}</p>
          {meta && <p className="mt-1 truncate text-[11px] text-fg-secondary">{meta}</p>}
        </div>
      </Link>
      {onRemove ? (
        <button
          type="button"
          aria-label="Remove from saved articles"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(article) }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-canvas text-fg-secondary transition-colors hover:border-red-7 hover:bg-red-2 hover:text-red-11 dark:hover:bg-red-3/30"
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      ) : (
        <BookmarkToggle saved={saved} pending={toggleSaved.isPending} onToggle={handleToggle} />
      )}
    </div>
  )
}
